import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Mail, User } from 'lucide-react';

const LOGO_URL = '/logo.png';

const RegisterPage = () => {
  const { user, register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src={LOGO_URL} alt="Grace The Geek Studios" className="h-14 w-auto object-contain" />
        </div>

        <div className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-500 text-center mb-6">
          // invite_only_signup
        </div>

        <div className="terminal-block">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
            <span className="terminal-dot bg-red-500/80"></span>
            <span className="terminal-dot bg-amber-400/80"></span>
            <span className="terminal-dot bg-emerald-400/80"></span>
            <span className="ml-auto font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-500">
              auth_register.exe
            </span>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-[1.05]">Create.</h1>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#735AE2] leading-[1.05]">Account.</h1>
              <p className="font-mono text-xs text-zinc-500 mt-3 uppercase tracking-widest">
                <span className="text-[#735AE2]">{'>'}</span> your email must be pre-invited by an admin
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                <User className="w-3 h-3 text-[#735AE2]" />
                Name (optional)
              </label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="register-name-input"
                className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none h-11"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                <Mail className="w-3 h-3 text-[#735AE2]" />
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gracethegeekstudios.com"
                autoComplete="email"
                required
                data-testid="register-email-input"
                className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none h-11"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                <Lock className="w-3 h-3 text-[#735AE2]" />
                Password (min 8 chars)
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                data-testid="register-password-input"
                className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none h-11"
              />
            </div>

            {error && (
              <div className="border border-rose-500/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200" data-testid="register-error">
                <span className="text-rose-400">{'>'}</span> {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-btn"
              className="w-full bg-[#735AE2] text-white hover:bg-[#8A75EA] rounded-none px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] font-medium h-12 disabled:opacity-50"
            >
              {submitting ? 'Creating account...' : 'Create account'}
            </Button>

            <div className="pt-4 border-t border-white/[0.06] font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between">
              <span>// already registered?</span>
              <Link to="/login" className="text-[#735AE2] hover:text-[#9D8AEC]" data-testid="login-link">
                {'<'} sign in
              </Link>
            </div>
          </form>
        </div>

        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-600 text-center mt-8">
          // GRACE THE GEEK STUDIOS · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
